import { SDKEnv, SDKStatusCode } from "@nebula-sdk/compose";
import { NebulaSiteService } from "./core";
import message from "./message";
import type {
  SDKInitArgs, SDKInitResult, SDKPayResult, SDKRequestOptions, SDKResult,
} from "@nebula-sdk/compose";
import type { App } from "vue";

export class NebulaSiteWechatService {
  public constructor() {
    this.core = new NebulaSiteService();
  }

  private readonly core: NebulaSiteService;

  private success(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message !== true || !args.result.msg) return;
    message.info({ message: args.result.msg ?? "成功" });
  }

  private error(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message === false || !args.result.msg) return;
    message.info({ message: args.result.msg ?? "错误" });
  }

  public async scopeRequestTrack(args: {
    eventGroup?: string;
    eventLevel?: "info" | "warn" | "error";
    eventId: string;
    eventName: string;
    eventValue: Record<string, unknown>;
  }): Promise<SDKResult<null>> {
    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };

    return await this.core.track({
      appId: this.core.options?.config.nbAppId ?? "",
      appVersion: this.core.version,
      appChannel: SDKEnv.Wechat,
      userId: this.core.userInfo?.nbOpenId ?? "",
      userTag: this.core.userInfo?.openId ?? "",
      deviceId: this.core.deviceId,
      deviceModel: navigator.userAgentData?.platform ?? "unknown",
      osType: this.core.platform,
      osVersion: navigator.userAgent,
      networkType: navigator.connection?.effectiveType ?? "unknown",
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.core.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  public async scopeRequestPassport<T = any>(
    args: SDKRequestOptions & { message?: boolean },
  ): Promise<SDKResult<T>> {
    const result = await this.core.requestPassport<T>(args);

    if (result.status === "success") {
      this.success({ message: args.message, result });
    } else {
      this.error({ message: args.message, result });
    }

    void this.scopeRequestTrack({
      eventId: args.url,
      eventName: "通行证请求",
      eventGroup: "wechat-mini-game",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  public parseQueryJson(): Record<string, string> {
    return this.core.parseJson(window.location.search.slice(1));
  }

  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    return await this.core.init(args);
  }

  public async pay(args: {
    appId: string;
    timeStamp: string;
    nonceStr: string;
    package: string;
    signType: string;
    paySign: string;
  }): Promise<SDKPayResult> {
    return await new Promise((resolve) => {
      window.WeixinJSBridge.invoke("getBrandWCPayRequest", args, (result) => {
        if (result.err_msg === "get_brand_wcpay_request:cancel") {
          void this.scopeRequestTrack({
            eventId: "pay",
            eventGroup: "payment",
            eventName: "微信客服网页支付取消",
            eventValue: { args, result },
            eventLevel: "info",
          });
          resolve({
            status: "error",
            code: SDKStatusCode.PayServiceCancelError,
            msg: "微信客服网页支付取消",
            data: null,
          });
          return;
        }

        if (result.err_msg === "get_brand_wcpay_request:fail") {
          void this.scopeRequestTrack({
            eventId: "pay",
            eventGroup: "payment",
            eventName: "微信客服网页支付失败",
            eventValue: { args, result },
            eventLevel: "error",
          });
          resolve({
            status: "error",
            code: SDKStatusCode.PayError,
            msg: "微信客服网页支付失败",
            data: null,
          });
          return;
        }

        void this.scopeRequestTrack({
          eventId: "pay",
          eventGroup: "payment",
          eventName: "微信客服网页支付成功",
          eventValue: { args, result },
          eventLevel: "info",
        });

        window.WeixinJSBridge.call("closeWindow");

        resolve({
          status: "success",
          code: SDKStatusCode.Success,
          msg: "微信客服网页支付成功",
          data: null,
        });
      });
    });
  }

  public install(app: App): void {
    Object.defineProperty(app.config.globalProperties, "$wechat", {
      enumerable: true,
      get: () => this,
    });
  }
}

export default new NebulaSiteWechatService();
